import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { platformApiFetch } from './platformApi';
import { usePlatformAuth } from '../context/PlatformAuthContext';

function statusColor(s) {
  if (s === 'active') return 'success';
  if (s === 'deprecated') return 'default';
  return 'warning';
}

export default function PlatformFeatureFlagDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = usePlatformAuth();
  const isSuper = user?.role === 'super_admin';

  const [flag, setFlag] = useState(null);
  const [plansCatalog, setPlansCatalog] = useState([]);
  const [tenantsCatalog, setTenantsCatalog] = useState([]);
  const [planEnabled, setPlanEnabled] = useState({});
  const [tenantRows, setTenantRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingPlans, setSavingPlans] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addTenantId, setAddTenantId] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [fr, pr, tr] = await Promise.all([
        platformApiFetch(`/api/platform/feature-flags/${id}/assignments`),
        platformApiFetch('/api/platform/plans?pageSize=200&status=active&sort=name&order=asc'),
        platformApiFetch('/api/platform/tenants?pageSize=500&sort=tenant_name&order=asc')
      ]);
      const fj = await fr.json().catch(() => ({}));
      const pj = await pr.json().catch(() => ({}));
      const tj = await tr.json().catch(() => ({}));
      if (!fr.ok) throw new Error(fj.error || 'Failed to load');
      setFlag(fj.featureFlag);
      if (pr.ok) setPlansCatalog(pj.plans || []);
      if (tr.ok) setTenantsCatalog(tj.tenants || []);

      const planMap = {};
      (fj.assignments?.plans || []).forEach((a) => {
        planMap[a.plan_id] = a.enabled;
      });
      const pe = {};
      (pj.plans || []).forEach((p) => {
        pe[p.id] = planMap[p.id] === true;
      });
      setPlanEnabled(pe);

      setTenantRows(
        (fj.assignments?.tenants || []).map((t) => ({
          tenant_id: t.tenant_id,
          tenant_name: t.tenant_name,
          tenant_code: t.tenant_code,
          enabled: t.enabled
        }))
      );
    } catch (e) {
      setError(e.message);
      setFlag(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const savePlanAssignments = async () => {
    setSavingPlans(true);
    setError(null);
    const assignmentsPayload = plansCatalog.map((p) => ({
      plan_id: p.id,
      enabled: Boolean(planEnabled[p.id])
    }));
    try {
      const res = await platformApiFetch(`/api/platform/feature-flags/${id}/plan-assignments`, {
        method: 'PUT',
        body: { assignments: assignmentsPayload }
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Save failed');
    } catch (e) {
      setError(e.message);
    } finally {
      setSavingPlans(false);
    }
  };

  const saveTenantAssignments = async (rows) => {
    setError(null);
    const payload = (rows || tenantRows).map((t) => ({
      tenant_id: t.tenant_id,
      enabled: t.enabled !== false
    }));
    try {
      const res = await platformApiFetch(`/api/platform/feature-flags/${id}/tenant-assignments`, {
        method: 'PUT',
        body: { assignments: payload }
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Save failed');
    } catch (e) {
      setError(e.message);
    }
  };

  const addTenantRow = async () => {
    if (!addTenantId) return;
    const tid = Number(addTenantId);
    const t = tenantsCatalog.find((x) => x.id === tid);
    if (!t) return;
    if (tenantRows.some((r) => r.tenant_id === tid)) {
      setAddOpen(false);
      return;
    }
    const next = [
      ...tenantRows,
      {
        tenant_id: tid,
        tenant_name: t.tenant_name,
        tenant_code: t.tenant_code,
        enabled: true
      }
    ];
    setTenantRows(next);
    setAddOpen(false);
    setAddTenantId('');
    await saveTenantAssignments(next);
  };

  const removeTenantRow = async (tenantId) => {
    const next = tenantRows.filter((r) => r.tenant_id !== tenantId);
    setTenantRows(next);
    await saveTenantAssignments(next);
  };

  const toggleTenant = async (tenantId, enabled) => {
    const next = tenantRows.map((r) => (r.tenant_id === tenantId ? { ...r, enabled } : r));
    setTenantRows(next);
    await saveTenantAssignments(next);
  };

  const removeDraft = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await platformApiFetch(`/api/platform/feature-flags/${id}`, { method: 'DELETE', body: {} });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Delete failed');
      setConfirmDelete(false);
      navigate('/platform/feature-flags');
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !flag) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!flag) return null;

  const availableTenantsToAdd = tenantsCatalog.filter((t) => !tenantRows.some((r) => r.tenant_id === t.id));

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Typography variant="h4">{flag.feature_name}</Typography>
          <Typography variant="body2" fontFamily="monospace" color="text.secondary">
            {flag.feature_code}
          </Typography>
          <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip size="small" label={flag.status} color={statusColor(flag.status)} />
            <Chip size="small" label={flag.is_global ? 'Global' : 'Scoped'} />
            <Chip size="small" label={`rollout: ${flag.rollout_type}`} />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Button variant="outlined" onClick={() => navigate('/platform/feature-flags')}>
            Back
          </Button>
          {isSuper && (
            <Button variant="contained" onClick={() => navigate(`/platform/feature-flags/${id}/edit`)}>
              Edit definition
            </Button>
          )}
          {isSuper && flag.status === 'draft' && (
            <Button color="error" variant="outlined" onClick={() => setConfirmDelete(true)}>
              Delete draft
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          Description
        </Typography>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {flag.description || '—'}
        </Typography>
      </Paper>

      {user && (
        <>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6">Plan access</Typography>
              {isSuper && (
                <Button variant="contained" size="small" onClick={savePlanAssignments} disabled={savingPlans}>
                  {savingPlans ? <CircularProgress size={20} /> : 'Save plan mapping'}
                </Button>
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              When the flag is <strong>not</strong> global, tenants inherit from their current plan if enabled here.
              Disabled plan rows block the feature for that plan unless a tenant override enables it.
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Plan</TableCell>
                  <TableCell align="right">Enabled</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {plansCatalog.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>
                      {p.name} <Typography variant="caption">({p.code})</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Switch
                        checked={Boolean(planEnabled[p.id])}
                        disabled={!isSuper}
                        onChange={(e) => setPlanEnabled((prev) => ({ ...prev, [p.id]: e.target.checked }))}
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {!plansCatalog.length && (
                  <TableRow>
                    <TableCell colSpan={2}>
                      <Typography color="text.secondary">No active plans loaded.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>

          <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6">Tenant overrides</Typography>
              {isSuper && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setAddOpen(true)}
                  disabled={!availableTenantsToAdd.length}
                >
                  Add tenant
                </Button>
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Tenant rows override plan mapping. Use to force on or off for a specific organization.
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Tenant</TableCell>
                  <TableCell align="center">Enabled</TableCell>
                  <TableCell align="right"> </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tenantRows.map((r) => (
                  <TableRow key={r.tenant_id}>
                    <TableCell>
                      {r.tenant_name}{' '}
                      <Typography variant="caption" color="text.secondary">
                        ({r.tenant_code})
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        checked={r.enabled !== false}
                        disabled={!isSuper}
                        onChange={(e) => toggleTenant(r.tenant_id, e.target.checked)}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {isSuper && (
                        <IconButton size="small" color="error" onClick={() => removeTenantRow(r.tenant_id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {!tenantRows.length && (
                  <TableRow>
                    <TableCell colSpan={3}>
                      <Typography color="text.secondary">No tenant overrides.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}

      <Paper sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Created
            </Typography>
            <Typography variant="body2">{flag.created_at ? new Date(flag.created_at).toLocaleString() : '—'}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Updated
            </Typography>
            <Typography variant="body2">{flag.updated_at ? new Date(flag.updated_at).toLocaleString() : '—'}</Typography>
          </Grid>
        </Grid>
      </Paper>

      <Dialog open={addOpen} onClose={() => setAddOpen(false)}>
        <DialogTitle>Add tenant override</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1, minWidth: 280 }}>
            <InputLabel>Tenant</InputLabel>
            <Select label="Tenant" value={addTenantId} onChange={(e) => setAddTenantId(e.target.value)}>
              {availableTenantsToAdd.map((t) => (
                <MenuItem key={t.id} value={String(t.id)}>
                  {t.tenant_name} ({t.tenant_code})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={addTenantRow} disabled={!addTenantId}>
            Add & save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmDelete} onClose={() => !busy && setConfirmDelete(false)}>
        <DialogTitle>Delete draft feature flag?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)} disabled={busy}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={removeDraft} disabled={busy}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
